import { useEffect } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../../components/Logo';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../components/ui/accordion';
import {
  FileText, PencilSimpleLine, DeviceMobile, ShieldCheck, Clock, ArrowRight, Check,
  Globe, Receipt, Warning, ChartBar, Calculator, CaretRight, House, Archive, CloudArrowDown, X,
} from '@phosphor-icons/react';
import { seoPages } from './seoPagesData';

const ICONS = {
  FileText, PencilSimpleLine, DeviceMobile, ShieldCheck, Clock, Globe, Receipt,
  Warning, ChartBar, Calculator, House, Archive, CloudArrowDown,
};

const BASE_URL = 'https://contratti.vitadahost.it';

function setSeoTags(data) {
  const url = `${BASE_URL}/${data.slug}`;
  document.title = data.title;

  const setMeta = (selector, attr, content) => {
    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement('meta');
      const [key, val] = selector.replace('meta[', '').replace(']', '').split('=');
      el.setAttribute(key, val.replace(/"/g, ''));
      document.head.appendChild(el);
    }
    el.setAttribute(attr, content);
  };

  setMeta('meta[name="description"]', 'content', data.description);
  setMeta('meta[name="keywords"]', 'content', data.keywords);
  setMeta('meta[property="og:title"]', 'content', data.title);
  setMeta('meta[property="og:description"]', 'content', data.description);
  setMeta('meta[property="og:url"]', 'content', url);
  setMeta('meta[name="twitter:title"]', 'content', data.title);
  setMeta('meta[name="twitter:description"]', 'content', data.description);

  // Canonical
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = url;

  // JSON-LD
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: data.title,
        description: data.description,
        url,
        inLanguage: 'it-IT',
        author: { '@type': 'Organization', name: 'Vita da Host', url: 'https://www.vitadahost.it' },
        publisher: {
          '@type': 'Organization',
          name: 'Vita da Host',
          logo: { '@type': 'ImageObject', url: `${BASE_URL}/icon.png` },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        datePublished: '2026-02-20',
        dateModified: '2026-02-20',
      },
      {
        '@type': 'FAQPage',
        mainEntity: data.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
          { '@type': 'ListItem', position: 2, name: data.breadcrumbName, item: url },
        ],
      },
    ],
  };

  let script = document.getElementById('seo-jsonld');
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'seo-jsonld';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(jsonLd);
}

export default function SeoLandingPage({ slug }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const data = seoPages[slug];

  useEffect(() => {
    if (data) {
      setSeoTags(data);
      window.scrollTo(0, 0);
    }
    return () => {
      // Cleanup JSON-LD when leaving
      const script = document.getElementById('seo-jsonld');
      if (script) script.remove();
    };
  }, [data]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid={`seo-page-${slug}`}>
      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-b border-zinc-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="seo-home-link">
            <Logo />
          </Link>
          <div className="flex items-center gap-1 sm:gap-3">
            <button
              onClick={() => navigate('/login')}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold uppercase tracking-wider text-zinc-700 hover:text-zinc-900 transition-colors"
              data-testid="seo-login-btn"
            >
              Accedi
            </button>
            <button
              onClick={() => navigate(user ? '/dashboard' : '/register')}
              className="px-3 sm:px-5 py-2 text-xs sm:text-sm font-bold uppercase tracking-wider bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
              data-testid="seo-cta-nav-btn"
            >
              {user ? 'Dashboard' : 'Inizia gratis'}
            </button>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="pt-20 sm:pt-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto py-3 sm:py-4">
          <nav className="flex items-center gap-1.5 text-xs text-zinc-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-zinc-900 transition-colors" data-testid="breadcrumb-home">Home</Link>
            <CaretRight size={12} weight="bold" />
            <span className="text-zinc-900 font-medium">{data.breadcrumbName}</span>
          </nav>
        </div>
      </div>

      {/* HERO */}
      <section className={`bg-gradient-to-br ${data.accentColor} text-white px-4 sm:px-6 py-12 sm:py-20 relative overflow-hidden`}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%2260%22%20height=%2260%22%20viewBox=%220%200%2060%2060%22%3E%3Cpath%20fill=%22%23ffffff%22%20fill-opacity=%220.03%22%20d=%22M30%2030l-15-15v30l15-15zm0%200l15%2015V15L30%2030z%22/%3E%3C/svg%3E')] opacity-50" />
        <div className="max-w-6xl mx-auto relative">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur px-3 py-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            {data.badge}
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] max-w-3xl mb-5" data-testid="seo-h1">
            {data.h1}
          </h1>
          <p className="text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl mb-8">
            {data.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center justify-center gap-3 bg-white text-zinc-900 font-bold uppercase tracking-wider text-xs sm:text-sm px-6 sm:px-8 py-3.5 sm:py-4 hover:bg-zinc-100 transition-all hover:gap-4"
              data-testid="seo-hero-cta"
            >
              Inizia gratis — 7 giorni <ArrowRight size={18} weight="bold" />
            </button>
            <Link
              to="/#faq"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-bold uppercase tracking-wider text-xs sm:text-sm px-6 sm:px-8 py-3.5 sm:py-4 hover:bg-white/10 transition-all"
            >
              Leggi le FAQ
            </Link>
          </div>

          {/* Stats */}
          {data.stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-10 sm:mt-14 pt-8 border-t border-white/15">
              {data.stats.map((s, i) => (
                <div key={i} className="text-left">
                  <p className="text-2xl sm:text-3xl font-black text-white">{s.value}</p>
                  <p className="text-[11px] sm:text-xs text-white/60 font-medium mt-0.5 uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-zinc-50">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-500 mb-3">Il problema</p>
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-8 max-w-2xl">
            {data.problem.title}
          </h2>
          <ul className="space-y-3 sm:space-y-4">
            {data.problem.items.map((it, i) => (
              <li key={i} className="flex items-start gap-3 bg-white border border-zinc-200 p-4 sm:p-5">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mt-0.5">
                  <X size={14} weight="bold" className="text-red-500" />
                </div>
                <p className="text-sm sm:text-base text-zinc-700 leading-relaxed">{it}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-12 sm:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">La soluzione</p>
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-10 max-w-2xl">
            Tutto quello che ti serve, in un'unica piattaforma
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
            {data.features.map((f, i) => {
              const Icon = ICONS[f.icon] || FileText;
              return (
                <article key={i} className="bg-white border border-zinc-200 p-6 sm:p-8 hover:border-zinc-900 transition-all group">
                  <div className={`inline-flex w-12 h-12 items-center justify-center bg-gradient-to-br ${data.accentColor} text-white mb-5`}>
                    <Icon size={22} weight="bold" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-black text-zinc-900 mb-2 tracking-tight">{f.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{f.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* MID-CTA */}
      <section className="py-14 sm:py-20 bg-zinc-900 text-white px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-4">
            Provalo gratis per 7 giorni
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 mb-8 max-w-xl mx-auto">
            Nessuna carta richiesta. Tutte le funzioni sbloccate. Se non ti piace, chiudi l'account in un click.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="inline-flex items-center gap-3 bg-white text-zinc-900 font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-zinc-100 transition-all hover:gap-4"
            data-testid="seo-mid-cta"
          >
            Inizia gratis <ArrowRight size={18} weight="bold" />
          </button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Domande frequenti</p>
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-8">
            Rispondiamo subito ai tuoi dubbi
          </h2>
          <Accordion type="single" collapsible className="space-y-2">
            {data.faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-zinc-200 bg-white px-4 sm:px-6">
                <AccordionTrigger className="text-left font-bold text-sm sm:text-base text-zinc-900 hover:no-underline py-4">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-600 leading-relaxed pb-4">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* INTERNAL LINKS */}
      {data.internalLinks && data.internalLinks.length > 0 && (
        <section className="py-12 sm:py-20 bg-zinc-50 px-4 sm:px-6 border-t border-zinc-200">
          <div className="max-w-6xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Potrebbe interessarti</p>
            <h2 className="text-xl sm:text-3xl font-black tracking-tight mb-8">
              Scopri gli altri strumenti per host italiani
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {data.internalLinks.map((s) => {
                const related = seoPages[s];
                if (!related) return null;
                return (
                  <Link
                    key={s}
                    to={`/${s}`}
                    className="group bg-white border border-zinc-200 p-4 sm:p-5 hover:border-zinc-900 transition-all"
                    data-testid={`internal-link-${s}`}
                  >
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">{related.badge}</p>
                    <p className="font-bold text-sm text-zinc-900 leading-snug group-hover:underline">{related.breadcrumbName}</p>
                    <div className="flex items-center gap-1 text-xs text-zinc-500 mt-2 group-hover:text-zinc-900 transition-colors">
                      Scopri <ArrowRight size={12} weight="bold" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className={`py-14 sm:py-20 bg-gradient-to-br ${data.accentColor} text-white px-4 sm:px-6`}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-4">
            {data.breadcrumbName}: prova Locazione Turistica oggi
          </h2>
          <p className="text-sm sm:text-base text-white/80 mb-8">
            7 giorni di prova gratuita. Poi €6,99/mese oppure €49,99/anno (risparmio 40%).
          </p>
          <button
            onClick={() => navigate('/register')}
            className="inline-flex items-center gap-3 bg-white text-zinc-900 font-bold uppercase tracking-wider text-sm px-10 py-5 hover:bg-zinc-100 transition-all hover:gap-4"
            data-testid="seo-final-cta"
          >
            Inizia gratis <ArrowRight size={20} weight="bold" />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-zinc-900 text-white px-4 sm:px-6 py-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-6 justify-between items-start sm:items-center">
            <div>
              <Logo className="[&_*]:text-white" />
              <p className="text-xs text-zinc-500 mt-3">
                © {new Date().getFullYear()} Vita da Host · P.IVA IT02903680995 · Genova, Italia
              </p>
            </div>
            <div className="flex flex-wrap gap-4 sm:gap-6 text-xs text-zinc-400">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contatti</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termini</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
